import React, { useState, useRef, useEffect, useMemo } from 'react';

import * as uuid from 'uuid';
import { useDropzone } from 'react-dropzone';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Input from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';

import UploadIcon from '@mui/icons-material/Upload';

import uploadFile from '../util/upload';
import { DEFAULT_ACCEPT_OBJECT, getAcceptedExtensions } from '../util/acceptedFormats.js';

const baseStyle = {
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: '20px',
	borderWidth: 2,
	borderRadius: 2,
	borderColor: '#eeeeee',
	borderStyle: 'dashed',
	backgroundColor: '#fafafa',
	color: '#bdbdbd',
	outline: 'none',
	transition: 'border .24s ease-in-out',
};

const ACCEPTED_TYPES = DEFAULT_ACCEPT_OBJECT;

const Dropzone = ({ label, onChange, accept }) => {
	const effectiveAccept = accept || ACCEPTED_TYPES;
	const acceptedExtensions = getAcceptedExtensions(effectiveAccept);
	const {
		getRootProps,
		getInputProps,
		isFocused,
		isDragAccept,
		isDragReject,
	} = useDropzone({ accept: effectiveAccept, onDrop: onChange });

	const style = useMemo(() => ({
		...baseStyle,
		...(isFocused ? { borderColor: '#2196f3' } : {}),
		...(isDragAccept ? { borderColor: 'rgba(0, 206, 124, 1)' } : {}),
		...(isDragReject ? { borderColor: 'rgba(255, 71, 87,1.0)' } : {}),
	}), [
		isFocused,
		isDragAccept,
		isDragReject,
	]);

	return (
		<Box sx={{ marginBottom: 2 }}>
			{label && <Typography variant='caption'>{label}</Typography>}
			<Box {...getRootProps({ style })}>
				<input {...getInputProps()} />
				<p>
					Drag and drop a file here, or click to select one
					{acceptedExtensions.length > 0 && ` (${acceptedExtensions.join(', ')})`}
				</p>
			</Box>
		</Box>
	);
};

const Upload = ({
	uploadUrl = '/api/files',
	value,
	name,
	label,
	placeholder,
	disabled,
	size,
	dropzone,
	onChange,
	fileType,
	isPublic = false,
	maximumFileSize, // in MB
	addPendingAction = () => { },
	removePendingAction = () => { },
	error: initialError,
	helperText,
	accept,
}) => {
	const inputRef = useRef(null);
	const [fileName, setFileName] = useState('');
	const [progress, setProgress] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(initialError);

	useEffect(() => {
		if (value) setFileName((value || {}).fileName);
	}, [value]);

	const clearUploadState = () => {
		setFileName(null);
		setProgress(0);
		setLoading(false);
	};

	const handleChange = async (files) => {
		if (!files || !files.length) {
			clearUploadState();
			setError(null);
			onChange({ target: { name, value: null } });
			return;
		}

		const [file] = files;
		if (maximumFileSize && file.size > maximumFileSize * 1024 * 1024) {
			clearUploadState();
			setError(`File exceeds the maximally allowed size of ${maximumFileSize} MB.`);
			onChange({ target: { name, value: null } });
			return;
		}


		setFileName(file.name);
		setProgress(0);
		setError(null);
		setLoading(true);

		const action = uuid.v4();
		try {
			addPendingAction(action);
			const arrayBuffer = await file.arrayBuffer();
			const uploadedFile = await uploadFile(uploadUrl, arrayBuffer, isPublic, {
				progressHandler: setProgress,
				contentType: file.type,
				filename: file.name,
				type: fileType || 'generic',
			});
			removePendingAction(action);
			return onChange({ target: { name, value: uploadedFile } });
		} catch (err) {
			onChange({ target: { name, value: null } });
			setFileName(null);
			setError(err);
		} finally {
			removePendingAction(action);
			setLoading(false);
		}
	};

	const UploadField = styled(FormControl, {
		name: 'MuiTextField',
		slot: 'Root',
		overridesResolver: (props, styles) => styles.root,
	})({});

	const triggerInput = () => {
		if (inputRef.current && !disabled && !loading) inputRef.current.click();
	};

	if (dropzone && !fileName) return (
		<Dropzone
			label={label}
			loading={loading}
			error={error}
			progress={progress}
			onChange={handleChange}
			fileName={fileName}
			accept={accept}
		/>
	);

	return (
		<UploadField
			sx={{ marginBottom: 2 }}
			fullWidth
			onClick={triggerInput}
		>
			<InputLabel shrink={true} error={!!error}>
				{label}
			</InputLabel>
			<Input
				notched={true}
				fullWidth={true}
				error={!!error}
				label={label}
				type='text'
				size={size}
				inputProps={{
					readOnly: true,
				}}
				style={{ zIndex: 2, cursor: 'pointer' }}
				value={fileName || placeholder || 'No file selected'}
				variant='outlined'
				endAdornment={loading ? <CircularProgress size={24} variant='determinate' value={progress} /> : <UploadIcon />}
				disabled={loading || disabled}
			/>

			{(error || helperText) && <FormHelperText error={!!error}>
				{<span>{error || helperText}</span>}
			</FormHelperText>}
			<input style={{ display: 'none' }} type='file' ref={inputRef} onChange={e => handleChange(e.target.files)} accept={Object.keys(accept || ACCEPTED_TYPES).join(', ')} />
		</UploadField>
	);
};

Upload.type = 'Upload';
Upload.spec = [{
	name: 'Upload',
	key: 'Upload',
	icon: 'Upload',
	hidden: true,
	title: 'File Upload',
	category: 'input',
	translatable: ['label'],
	fields: [{
		type: 'TextField',
		label: 'Label',
		name: 'label',
	}, {
		type: 'Checkbox',
		label: 'Required?',
		name: 'required',
	}],
}];
export default Upload;